import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  capitalize,
  Chip,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Layout from "../../components/Layout";
import { useT } from "../../i18n";
import React, { useCallback, useEffect, useState } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import {
  useAssignBaustelleZuRechnung,
  useGetZugewieseneBaustellen,
  useUpdateRechnung,
  useUpdateZugewieseneBaustelle,
} from "../../client/hooks";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "react-query";
import {
  Baustelle,
  Benutzer,
  Lieferant,
  Rechnung,
  RechnungDaten,
} from "../../api";
import {
  Field,
  Form,
  Formik,
  FormikHelpers,
  FormikValues,
  useField,
  useFormikContext,
} from "formik";
import * as yup from "yup";
import * as Yup from "yup";
import { Select, TextField } from "formik-mui";
import LieferantenSelect from "../../components/lieferanten/LieferantenSelect";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useGetAccessToken } from "../../client";
import { apiUrl } from "../../config";
import { useLocation, useNavigate, useParams } from "react-router";
import { useRechnung } from "../../client/queries";
import BaustellenEintrag, {
  ZugewieseneBaustelleUpdated,
} from "../../components/eingangsrechnungen/BaustellenEintrag";
import SubmitButton from "../../components/SubmitButton";
import DeleteRechnungenButton from "../../components/rechnungen/DeleteRechnungenButton";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import {
  RenderCurrentScaleProps,
  RenderZoomInProps,
  RenderZoomOutProps,
  zoomPlugin,
} from "@react-pdf-viewer/zoom";
import DublettenCheck from "../../components/rechnungen/DublettenCheck";
import SkontoFields from "../../components/rechnungen/SkontoFields";
import ErrorAlert from "../../components/ErrorAlert";
import { notPermitted } from "../../errors";
import Guard from "../../components/Guard";
import useShowFormikErrors from "../../hooks/useShowFormikErrors";
import ConfirmNavigationDialog from "../../components/ConfirmNavigationDialog";
import { useConfirmationNavDialog } from "../../hooks/useConfirmationNavDialog";
import LieferantenDatenDirtyChecker from "../../components/rechnungen/LieferantenDatenDirtyChecker";
import EingangsrechnungenZuweisungsListe from "../../components/eingangsrechnungen/EingangsrechnungenZuweisungsListe";
import StorniertWarnung from "../../components/rechnungen/StorniertWarnung";
import { EingangsrechnungProvider } from "../../contexts/EingangsrechnungContext";
import { useBaustellenEintragContext } from "../../contexts/BaustellenEintragContext";
import { ResendDatevButton } from "../../components/rechnungen/ResendDatevButton";
import { formatAsFloat, formatAsTimestamp } from "../../formatterHelpers";
import GutschriftenHinweis from "../../components/rechnungen/GutschriftenHinweis";

export default function RechnungsFormular() {
  const { t } = useT("eingangsrechnung");
  const { id: rechnungId = "" } = useParams<{
    id: string;
  }>();
  const { isLoading, data } = useGetAnhang(rechnungId) as UseQueryResult<Blob>;
  const rechnung = useRechnung(rechnungId);
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch =
    new URLSearchParams(location.search).get("returnSearch") ?? "";
  const [baustellenEintraege, setBaustellenEintraege] =
    useState<ZugewieseneBaustelleUpdated[]>();
  const fetchZugewieseneBaustellen = useGetZugewieseneBaustellen();
  const { mutateAsync } = useSaveRechnung(rechnungId);
  const createBaustellenEintrag = useAssignBaustelleZuRechnung();
  const updateBaustellenEintrag = useUpdateZugewieseneBaustelle();
  const zoomPluginInstance = zoomPlugin({ enableShortcuts: false });
  const [fileUrl, setFileUrl] = useState<string>();
  const [isLieferantenDatenDirty, setIsLieferantenDatenDirty] =
    useState<boolean>(false);

  const { showConfirmDialog, handleConfirmBack, handleCancelBack } =
    useConfirmationNavDialog({
      backRoute: `/eingangsrechnungen/${returnSearch}`,
    });

  useEffect(() => {
    if (data) {
      setFileUrl(URL.createObjectURL(data));
    }
  }, [isLoading]);

  const { updateBetrag } = useBaustellenEintragContext();

  useEffect(() => {
    if (baustellenEintraege) {
      baustellenEintraege.forEach((baustellenEintrag, index) => {
        if (baustellenEintrag.betrag) {
          const formattedValue = Number.parseFloat(
            String(baustellenEintrag.betrag).replace(".", "").replace(",", ".")
          );
          if (!isNaN(formattedValue)) {
            updateBetrag(index, formattedValue);
          }
        }
      });
    }
  }, [baustellenEintraege, updateBetrag]);

  useEffect(() => {
    const fetchZugewiesene = async () => {
      const baustellenData = await fetchZugewieseneBaustellen(rechnungId, {
        search: "",
        page: 0,
        limit: 100,
      });
      if (baustellenData.items.length > 0) {
        setBaustellenEintraege(
          baustellenData.items.map((item) => {
            return {
              id: item.id,
              rechnungsId: item.rechnungsId,
              baustelle: item.baustellenId,
              pruefer: item.prueferId,
              pruefStatus: item.pruefStatus,
              betrag: formatNumber(item.betrag),
              leistung: item.leistung,
            };
          })
        );
      } else {
        setBaustellenEintraege([
          {
            id: "",
            rechnungsId: rechnungId,
            baustelle: "",
            pruefer: "",
            betrag: "",
            pruefStatus: "offen",
            leistung: "",
          },
        ]);
      }
    };
    const fetchLieferant = async () => {
      if (rechnung.lieferantId === undefined || rechnung.lieferantId === "") {
        return;
      }
    };
    fetchZugewiesene();
    fetchLieferant();
  }, [rechnungId, fetchZugewieseneBaustellen]);

  let schema = yup
    .object()
    .shape({
      belegtyp: yup.string().required(),
      belegStatus: yup.string(),
      rechnungsnummer: yup.string(),
      rechnungsdatum: yup
        .date()
        .max(
          new Date("9999-12-31"),
          "Datum darf nicht über dem 31.12.9999 liegen!"
        ),
      rechnungseingang: yup
        .date()
        .max(
          new Date("9999-12-31"),
          "Datum darf nicht über dem 31.12.9999 liegen!"
        ),
      bruttobetrag: yup.string(),
      nettobetrag: yup.string(),
      rechnungsBetrag: yup.string(),
      rechnungsbetragAbweichend: yup.boolean(),
      umsatzsteuer: yup.string(),
      lieferant: yup.object(),
      kundennummer: yup.string(),
      steuer: yup.number(),
      steuernummer: yup.string(),
      umsatzsteuerId: yup.string(),
      pruefStatus: yup.string(),
      iban: yup.string(),
      geschaefspartnerKonto: yup.string(),
      bezahltAm: yup
        .date()
        .max(
          new Date("9999-12-31"),
          "Datum darf nicht über dem 31.12.9999 liegen!"
        ),
      skonto1InProzent: yup.string(),
      skontoInEuro: yup.string(),
      skontoBetrag: yup.string(),
      zahlbetrag: yup.string(),
      faelligkeitMitSkonto: yup
        .date()
        .max(
          new Date("9999-12-31"),
          "Datum darf nicht über dem 31.12.9999 liegen!"
        ),
      faelligkeitOhneSkonto: yup
        .date()
        .max(
          new Date("9999-12-31"),
          "Datum darf nicht über dem 31.12.9999 liegen!"
        ),
    })
    .when({
      is: (values: any) => values.belegtyp?.toLowerCase() !== "storno",
      then: (schema) =>
        schema.shape({
          baustellenEintrag: Yup.array()
            .of(
              Yup.object()
                .shape({
                  id: Yup.string(),
                  rechnungsId: Yup.string(),
                  baustelle: Yup.mixed().nullable(),
                  pruefer: Yup.mixed().nullable(),
                  pruefStatus: Yup.string(),
                  betrag: Yup.string(),
                  leistung: Yup.string(),
                })
                .test(
                  "baustelle-pruefer-betrag-required",
                  t("construction-site-required"),
                  function (value) {
                    if (!value) return true;

                    if (this.parent.belegtyp?.toLowerCase() === "storno") {
                      return true;
                    }

                    const isEmpty = (v: any) =>
                      v === null || v === undefined || v === "";

                    const hasBaustelle = !isEmpty(value.baustelle);
                    const hasPruefer = !isEmpty(value.pruefer);
                    const hasBetrag = !isEmpty(value.betrag);
                    const hasLeistung = !isEmpty(value.leistung);

                    if (
                      hasBaustelle ||
                      hasPruefer ||
                      hasBetrag ||
                      hasLeistung
                    ) {
                      return (
                        !isEmpty(value.baustelle) &&
                        !isEmpty(value.pruefer) &&
                        !isEmpty(value.betrag)
                      );
                    }

                    return true;
                  }
                )
            )
            .min(0, ""),
        }),
      otherwise: (schema) =>
        schema.shape({
          baustellenEintrag: Yup.array().of(
            Yup.object().shape({
              id: Yup.string().nullable(),
              rechnungsId: Yup.string().nullable(),
              baustelle: Yup.mixed().nullable(),
              pruefer: Yup.mixed().nullable(),
              pruefStatus: Yup.string().nullable(),
              betrag: Yup.string().nullable(),
              leistung: Yup.string().nullable(),
            })
          ),
        }),
    });

  interface ZugewieseneBaustelleUpdated {
    id: string;
    rechnungsId: string | undefined;
    baustelle: any | undefined;
    pruefer: any | undefined;
    pruefStatus: string | undefined;
    betrag: string;
    leistung: string | undefined;
  }

  const onSubmit = useCallback(
    async (
      values: {
        belegtyp: string;
        belegStatus: string;
        rechnungsnummer: string;
        rechnungsdatum: string;
        rechnungseingang: string;
        bruttobetrag: string;
        rechnungsBetrag: string;
        rechnungsbetragAbweichend: boolean;
        nettobetrag: string;
        umsatzsteuer: string;
        lieferant: any;
        kundennummer: string;
        steuer: number;
        steuernummer: string;
        umsatzsteuerId: string;
        pruefStatus: string;
        iban: string;
        geschaefspartnerKonto: string;
        bezahltAm: string;
        skonto1InProzent: string;
        skontoInEuro: string;
        faelligkeitMitSkonto: string;
        faelligkeitOhneSkonto: string;
        skontoBetrag: string;
        zahlbetrag: string;
        baustellenEintrag: ZugewieseneBaustelleUpdated[];
      },
      formikHelpers: FormikHelpers<{
        belegtyp: string;
        belegStatus: string;
        rechnungsnummer: string;
        rechnungsdatum: string;
        rechnungseingang: string;
        bruttobetrag: string;
        rechnungsBetrag: string;
        rechnungsbetragAbweichend: boolean;
        nettobetrag: string;
        umsatzsteuer: string;
        lieferant: any;
        kundennummer: string;
        steuer: number;
        steuernummer: string;
        umsatzsteuerId: string;
        pruefStatus: string;
        iban: string;
        geschaefspartnerKonto: string;
        bezahltAm: string;
        skonto1InProzent: string;
        skontoInEuro: string;
        faelligkeitMitSkonto: string;
        faelligkeitOhneSkonto: string;
        skontoBetrag: string;
        zahlbetrag: string;
        baustellenEintrag: ZugewieseneBaustelleUpdated[];
      }>
    ) => {
      const { setSubmitting, setStatus } = formikHelpers;
      await updateBaustellenEintraege(
        values.bezahltAm,
        values.baustellenEintrag
      );
      await mutateAsync({
        belegTyp: values.belegtyp,
        belegStatus: values.belegStatus,
        rechnungsNummer: values.rechnungsnummer,
        rechnungsDatum: values.rechnungsdatum
          ? values.rechnungsdatum + "T00:00:00Z"
          : values.rechnungsdatum,
        bruttoBetrag: parseFloat(
          values.bruttobetrag.replace(".", "").replace(",", ".")
        ),
        rechnungsBetrag: parseFloat(
          values.rechnungsBetrag.replace(".", "").replace(",", ".")
        ),
        rechnungsbetragAbweichend: values.rechnungsbetragAbweichend,
        nettoBetrag: parseFloat(
          values.nettobetrag.replace(".", "").replace(",", ".")
        ),
        lieferantId:
          typeof values.lieferant === "string"
            ? values.lieferant
            : (values.lieferant as Lieferant)?.id ?? "",
        kundenNummer: values.kundennummer,
        steuer: values.steuer,
        steuernummer: values.steuernummer,
        umsatzsteuerId: values.umsatzsteuerId,
        iban: values.iban,
        geschaeftspartnerKonto: values.geschaefspartnerKonto,
        bezahltAm: values.bezahltAm
          ? values.bezahltAm + "T00:00:00Z"
          : values.bezahltAm,
        skontoProzent: parseFloat(
          values.skonto1InProzent.replace(".", "").replace(",", ".")
        ),
        skontierfaehigerBetrag: parseFloat(
          values.skontoInEuro.replace(".", "").replace(",", ".")
        ),
        skontoBetrag: parseFloat(
          values.skontoBetrag.replace(".", "").replace(",", ".")
        ),
        skontoDatum: values.faelligkeitMitSkonto
          ? values.faelligkeitMitSkonto + "T00:00:00Z"
          : values.faelligkeitMitSkonto,
        faelligOhneSkonto: values.faelligkeitOhneSkonto
          ? values.faelligkeitOhneSkonto + "T00:00:00Z"
          : values.faelligkeitOhneSkonto,
      });

      navigate(`/eingangsrechnungen/${returnSearch}`);
      setStatus(undefined);
      try {
      } catch (error: any) {
        setStatus(error.message);
      } finally {
        setSubmitting(false);
      }
    },
    []
  );

  async function updateBaustellenEintraege(
    bezahltAm: string,
    eintraege: ZugewieseneBaustelleUpdated[]
  ) {
    const baustellenPromises = eintraege.map((baustelle) => {
      const formattedBetrag = parseFloat(
        String(baustelle.betrag).replace(".", "").replace(",", ".")
      );
      const prueferId = (baustelle.pruefer as Benutzer)?.id ?? null;
      const baustellenId = (baustelle.baustelle as Baustelle)?.id ?? null;
      const baustelleData = {
        baustellenId: baustellenId,
        prueferId: prueferId,
        betrag: formattedBetrag,
        pruefStatus: baustelle.pruefStatus,
        leistung: baustelle.leistung,
      };
      if (baustelle.id) {
        return updateBaustellenEintrag(
          rechnungId,
          baustelle.id,
          { rechnungBeglichenAm: bezahltAm },
          baustelleData
        );
      } else {
        return createBaustellenEintrag(
          rechnungId,
          { rechnungBeglichenAm: bezahltAm },
          baustelleData
        );
      }
    });
    await Promise.all(baustellenPromises);
  }

  const formatNumber = (value: number | undefined) => {
    // If the value is null, undefined, or NaN, return an empty string
    if (value == null || isNaN(value)) return "";

    // Format the number with exactly two decimal places
    return value
      .toFixed(2)
      .replace(".", ",")
      .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  };
  const { CurrentScale, ZoomIn, ZoomOut } = zoomPluginInstance;

  // @ts-ignore
  return (
    <Layout title={t("audit")} onBackClick={() => navigate(-1)}>
      <ConfirmNavigationDialog
        open={showConfirmDialog}
        onConfirm={handleConfirmBack}
        onCancel={handleCancelBack}
        title={t("confirm-cancel-title")}
        message={t("confirm-cancel-message")}
      />
      <Guard
        permission={"eingangsrechnung:edit"}
        fallback={<ErrorAlert error={notPermitted()} />}
      >
        <Box
          sx={{
            marginTop: 2,
            padding: 3,
            marginLeft: 2,
            marginRight: 2,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={3} sx={{ padding: 3, height: "90vh" }}>
                <Box
                  sx={{
                    marginTop: 2,
                    height: "calc(100% - 48px)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Stack direction={"row"}>
                    <Typography variant="h5" gutterBottom>
                      {rechnung.geloeschtAm === undefined ? (
                        t("preview")
                      ) : (
                        <>
                          <>{t("preview")}</>
                          <Chip
                            size="small"
                            icon={<DeleteIcon />}
                            label={t("deleted")}
                            color="error"
                          />
                        </>
                      )}
                    </Typography>
                    <Stack
                      direction={"row"}
                      justifyContent="center"
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <ZoomOut>
                        {(props: RenderZoomOutProps) => (
                          <button
                            style={{
                              backgroundColor: "#c7333d",
                              border: "none",
                              borderRadius: "4px",
                              color: "#ffffff",
                              cursor: "pointer",
                              padding: "8px",
                            }}
                            onClick={props.onClick}
                          >
                            Zoom out
                          </button>
                        )}
                      </ZoomOut>
                      <Box sx={{ ml: 1, mr: 1 }}>
                        <CurrentScale>
                          {(props: RenderCurrentScaleProps) => (
                            <>{`${Math.round(props.scale * 100)}%`}</>
                          )}
                        </CurrentScale>
                      </Box>

                      <ZoomIn>
                        {(props: RenderZoomInProps) => (
                          <button
                            style={{
                              backgroundColor: "#c7333d",
                              border: "none",
                              borderRadius: "4px",
                              color: "#ffffff",
                              cursor: "pointer",
                              padding: "8px",
                            }}
                            onClick={props.onClick}
                          >
                            Zoom in
                          </button>
                        )}
                      </ZoomIn>
                    </Stack>
                  </Stack>

                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {fileUrl ? (
                      <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.0.279/build/pdf.worker.min.js">
                        <Viewer
                          fileUrl={fileUrl}
                          plugins={[zoomPluginInstance]}
                        />
                      </Worker>
                    ) : (
                      <Typography variant="body2" gutterBottom>
                        {t("no-pdf")}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Formik
                enableReinitialize={true}
                initialValues={{
                  belegtyp: rechnung.belegTyp ?? "",
                  belegStatus: rechnung.belegStatus ?? "",
                  rechnungsnummer: rechnung.rechnungsNummer ?? "",
                  rechnungsdatum: rechnung.rechnungsDatum?.split("T")[0] ?? "",
                  rechnungseingang: rechnung.timestamp?.split("T")[0] ?? "",
                  bruttobetrag: formatNumber(rechnung.bruttoBetrag) ?? "",
                  rechnungsBetrag: formatNumber(rechnung.rechnungsBetrag) ?? "",
                  rechnungsbetragAbweichend:
                    rechnung.rechnungsbetragAbweichend ?? false,
                  nettobetrag: formatNumber(rechnung.nettoBetrag) ?? "",
                  lieferant: rechnung.lieferantId ?? "",
                  umsatzsteuer: formatNumber(
                    (rechnung.bruttoBetrag ?? 0) - (rechnung.nettoBetrag ?? 0)
                  ),
                  steuer: rechnung.steuer ?? 19,
                  steuernummer: rechnung.steuernummer ?? "",
                  umsatzsteuerId: rechnung.umsatzsteuerId ?? "",
                  pruefStatus: rechnung.pruefStatus ?? "",
                  iban: rechnung.iban ?? "",
                  geschaefspartnerKonto: rechnung.geschaeftspartnerKonto ?? "",
                  bezahltAm: rechnung.bezahltAm?.split("T")[0] ?? "",
                  kundennummer: rechnung.kundenNummer ?? "",
                  skonto1InProzent: formatNumber(rechnung.skonto1Prozent) ?? "",
                  skontoInEuro:
                    formatNumber(rechnung.skontierfaehigerBetrag) ?? "",
                  skontoBetrag: formatNumber(rechnung.skontoBetrag) ?? "",
                  zahlbetrag: "",
                  faelligkeitMitSkonto:
                    rechnung.belegTyp?.toUpperCase() === "GUTSCHRIFT"
                      ? "9999-01-01"
                      : rechnung.skonto1Datum?.split("T")[0] ?? "",
                  faelligkeitOhneSkonto:
                    rechnung.faelligOhneSkonto?.split("T")[0] ?? "",
                  baustellenEintrag: baustellenEintraege?.length
                    ? baustellenEintraege.map((eintrag) => {
                        return {
                          id: eintrag.id,
                          rechnungsId: eintrag.rechnungsId,
                          baustelle: eintrag.baustelle,
                          pruefer: eintrag.pruefer,
                          betrag: eintrag.betrag,
                          pruefStatus: eintrag.pruefStatus,
                          leistung: eintrag.leistung,
                        };
                      })
                    : [],
                }}
                validationSchema={schema}
                onSubmit={onSubmit}
              >
                {({ isSubmitting, isValidating, isValid, dirty, values }) => {
                  return (
                    <>
                      <Form id="details-eingangsrechnung">
                        <LieferantenDatenDirtyChecker
                          setIsLieferantenDatenDirty={
                            setIsLieferantenDatenDirty
                          }
                        />
                        <RechnungsFormularFieldsContent
                          rechnungId={rechnungId}
                          rechnung={rechnung}
                          isSubmitting={isSubmitting}
                          dirty={dirty}
                          isValid={isValid}
                          isValidating={isValidating}
                          values={values}
                        />
                      </Form>
                    </>
                  );
                }}
              </Formik>
            </Grid>
          </Grid>
        </Box>
      </Guard>
    </Layout>
  );
}

function RechnungsFormularFieldsContent({
  rechnungId,
  rechnung,
  isSubmitting,
  isValidating,
  isValid,
  dirty,
  values,
}: {
  rechnungId: string;
  rechnung: Rechnung;
  isSubmitting: boolean;
  isValidating: boolean;
  isValid: boolean;
  dirty: boolean;
  values: FormikValues;
}) {
  const { t } = useT("eingangsrechnung");
  const navigate = useNavigate();
  const location = useLocation();
  const returnSearch =
    new URLSearchParams(location.search).get("returnSearch") ?? "";
  const createBaustellenEintrag = useAssignBaustelleZuRechnung();
  const updateBaustellenEintrag = useUpdateZugewieseneBaustelle();
  const { mutateAsync } = useSaveRechnung(rechnungId);
  const { setFieldValue, errors, validateForm } = useFormikContext();
  const [nettobetragField] = useField("nettobetrag");
  const [bruttobetragField] = useField("bruttobetrag");
  const [lieferantField] = useField("lieferant");
  const { tooltipMessage } = useShowFormikErrors({ errors });
  const [isRechnungsbetragChanged, setIsRechnungsbetragChanged] =
    useState<boolean>(false);

  const handleNewLieferantClick = () => {
    const currentHost = window.location.origin;
    window.open(`${currentHost}/neu-lieferant`, "_blank");
  };

  useEffect(() => {
    const nettoBetrag =
      nettobetragField.value.replace(".", "").replace(",", ".") ?? 0;
    const bruttoBetrag =
      bruttobetragField.value.replace(".", "").replace(",", ".") ?? 0;

    const umsatzBetrag = (bruttoBetrag - nettoBetrag).toFixed(2);

    setFieldValue("umsatzsteuer", umsatzBetrag);
  }, [nettobetragField.value, bruttobetragField.value]);

  async function updateBeleg(values: any) {
    await mutateAsync({
      belegTyp: values.belegtyp ?? "Eingangsrechnung",
      belegStatus: values.belegStatus ?? "offen",
      rechnungsNummer: values.rechnungsnummer,
      rechnungsDatum: values.rechnungsdatum
        ? values.rechnungsdatum + "T00:00:00Z"
        : values.rechnungsdatum,
      bruttoBetrag: parseFloat(
        values.bruttobetrag.replace(".", "").replace(",", ".")
      ),
      rechnungsBetrag: parseFloat(
        values.rechnungsBetrag.replace(".", "").replace(",", ".")
      ),
      rechnungsbetragAbweichend: values.rechnungsbetragAbweichend,
      nettoBetrag: parseFloat(
        values.nettobetrag.replace(".", "").replace(",", ".")
      ),
      lieferantId:
        typeof values.lieferant === "string"
          ? values.lieferant
          : (values.lieferant as Lieferant)?.id ?? "",
      kundenNummer: values.kundennummer,
      steuer: values.steuer,
      steuernummer: values.steuernummer,
      umsatzsteuerId: values.umsatzsteuerId,
      iban: values.iban,
      geschaeftspartnerKonto: values.geschaefspartnerKonto,
      bezahltAm: values.bezahltAm
        ? values.bezahltAm + "T00:00:00Z"
        : values.bezahltAm,
      skontoProzent: parseFloat(values.skonto1InProzent.replace(",", ".")),
      skontierfaehigerBetrag: parseFloat(values.skontoInEuro.replace(",", ".")),
      skontoBetrag: parseFloat(
        values.skontoBetrag.replace(".", "").replace(",", ".")
      ),
      skontoDatum: values.faelligkeitMitSkonto
        ? values.faelligkeitMitSkonto + "T00:00:00Z"
        : values.faelligkeitMitSkonto,
      faelligOhneSkonto: values.faelligkeitOhneSkonto
        ? values.faelligkeitOhneSkonto + "T00:00:00Z"
        : values.faelligkeitOhneSkonto,
    });
  }

  const rechnungBetragChanged = () => {
    setIsRechnungsbetragChanged(true);
  };
  const formatNumber = (num: number) =>
    new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);

  useEffect(() => {
    handleLieferantChange();
  }, [lieferantField.value]);

  const handleLieferantChange = () => {
    if (lieferantField.value) {
      if (!rechnung.steuernummer) {
        setFieldValue("steuernummer", lieferantField.value.steuernummer);
      }
      if (!rechnung.umsatzsteuerId) {
        setFieldValue("umsatzsteuerId", lieferantField.value.umsatzsteuerId);
      }
      if (!rechnung.kundenNummer) {
        setFieldValue("kundennummer", lieferantField.value.kundennummer);
      }
      if (!rechnung.geschaeftspartnerKonto) {
        setFieldValue(
          "geschaefspartnerKonto",
          lieferantField.value.lieferantennummer
        );
      }
    }
  };

  const updateBaustellenZuweisungen = async (
    baustellenEintrags: ZugewieseneBaustelleUpdated[]
  ) => {
    for (const baustelle of baustellenEintrags) {
      if (
        baustelle.pruefStatus === "offen" ||
        baustelle.pruefStatus === "in Prüfung"
      ) {
        const prueferId = (baustelle.pruefer as Benutzer)?.id ?? null;
        const baustellenId = (baustelle.baustelle as Baustelle)?.id ?? null;
        if (baustelle.id) {
          baustelle.pruefStatus = "in Prüfung";
          const formattedBetrag = parseFloat(
            String(baustelle.betrag).replace(".", "").replace(",", ".")
          );
          await updateBaustellenEintrag(
            rechnungId,
            baustelle.id,
            { rechnungBeglichenAm: values.bezahltAm },
            {
              baustellenId: baustellenId,
              prueferId: prueferId,
              leistung: baustelle.leistung,
              betrag: formattedBetrag,
              pruefStatus: "in Prüfung",
            }
          );
        } else {
          await createBaustellenEintrag(
            rechnungId,
            { rechnungBeglichenAm: values.bezahltAm },
            {
              baustellenId: baustellenId,
              prueferId: prueferId,
              leistung: baustelle.leistung,
              betrag: parseFloat(
                String(baustelle.betrag).replace(".", "").replace(",", ".")
              ),
              pruefStatus: "in Prüfung",
            }
          ).then((value) => {
            baustelle.id = value.id;
          });
        }
      }
    }
  };

  const isStornoTyp = values.belegtyp.toLowerCase() === "storno";

  const { sumAssignedNetto } = useBaustellenEintragContext();

  return (
    <EingangsrechnungProvider>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <StorniertWarnung
          storniertAm={rechnung.storniertAm ?? null}
          stornoRechnungId={rechnung?.stornoRechnungId ?? ""}
        />
        <DublettenCheck
          rechnungId={rechnungId}
          rechnungsnummer={values.rechnungsnummer}
          lieferantenId={
            typeof values.lieferant === "string"
              ? values.lieferant
              : (values.lieferant as Lieferant)?.id ?? ""
          }
        />
        <GutschriftenHinweis rechnungId={rechnung.id} />
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
          >
            <Typography>{t("invoice")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: ["1fr", "1fr 1fr 1fr 1fr"],
              }}
            >
              <Field name="belegStatus" type="hidden" />
              <Field
                name="belegtyp"
                component={Select}
                label={t("document-type") + " *"}
              >
                <MenuItem key="rechnungseingang" value="Eingangsrechnung">
                  {t("invoice")}
                </MenuItem>
                <MenuItem key="Gutschrift" value="Gutschrift">
                  {t("credit")}
                </MenuItem>
                <MenuItem key="Abbuchung" value="Abbuchung">
                  {t("debit")}
                </MenuItem>
                <MenuItem key="Storno" value="Storno">
                  {t("cancellation")}
                </MenuItem>
              </Field>
              <Field
                component={TextField}
                name="rechnungsnummer"
                label={t("invoice-number")}
              ></Field>
              <Field
                component={TextField}
                label={t("invoice-date")}
                name="rechnungsdatum"
                InputLabelProps={{ shrink: true }}
                type={"date"}
              />
              <Field
                component={TextField}
                label={t("invoice-entry")}
                name={"rechnungseingang"}
                type={"date"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gap: 2,
                gridTemplateColumns: ["1fr", "2fr 1fr 1fr"],
                alignItems: "center",
              }}
            >
              <Field
                component={LieferantenSelect}
                name="lieferant"
                label="Lieferant"
              ></Field>
              <Button
                variant={"outlined"}
                sx={{ height: "70%" }}
                onClick={handleNewLieferantClick}
              >
                <AddIcon />
                {t("new-supplier")}
              </Button>
            </Box>
            <Box
              sx={{
                mt: 2,
                display: "grid",
                gap: 2,
                gridTemplateColumns: ["1fr", "2fr 1fr 1fr "],
                alignItems: "center",
              }}
            >
              <Field
                component={TextField}
                name="iban"
                label={t("iban")}
              ></Field>
              <Field
                component={TextField}
                name="steuernummer"
                label={t("tax-number")}
              ></Field>
              <Field
                component={TextField}
                name="umsatzsteuerId"
                label={t("sales-tax-id")}
              ></Field>
              <Field
                component={TextField}
                name="kundennummer"
                label={t("supplier-customer-number")}
              ></Field>
              <Field
                component={TextField}
                name="geschaefspartnerKonto"
                label={t("business-partner-account")}
              ></Field>
              <Field
                component={TextField}
                label={t("invoice-payment-date")}
                name="bezahltAm"
                InputLabelProps={{ shrink: true }}
                type={"date"}
                disabled={
                  rechnung.belegTyp === "Eingangsrechnung" ||
                  rechnung.belegStatus !== "IN_DATEV"
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ArrowDropDownIcon />}
            aria-controls="panel2-content"
            id="panel2-header"
          >
            <Typography>{t("invoice-amount")}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: ["1fr", "1fr 1fr 1fr 1fr"],
              }}
            >
              <Field
                component={TextField}
                name="nettobetrag"
                label={t("net-amount-euro")}
                inputProps={{ style: { textAlign: "right" } }}
                helperText={`Zugeordnet [€]: ${
                  (sumAssignedNetto || 0) !== 0
                    ? sumAssignedNetto.toFixed(2).replace(".", ",")
                    : "0"
                }`}
              ></Field>
              <Field
                component={TextField}
                name="steuer"
                label={t("tax-rate")}
                inputProps={{ style: { textAlign: "right" } }}
              ></Field>
              <Field
                component={TextField}
                name="umsatzsteuer"
                label={t("sales-tax")}
                inputProps={{
                  style: { textAlign: "right" },
                }}
                disabled={true}
              ></Field>
              <Field
                component={TextField}
                name="bruttobetrag"
                label={t("gross-amount-euro")}
                inputProps={{ style: { textAlign: "right" } }}
              ></Field>
              <Field
                component={TextField}
                name="faelligkeitOhneSkonto"
                label={"Fälligkeit ohne Skonto"}
                type={"date"}
                InputLabelProps={{ shrink: true }}
              ></Field>
              <FormControlLabel
                sx={{
                  gridColumn: "span 2",
                }}
                control={
                  <Field
                    name="rechnungsbetragAbweichend"
                    as={Switch}
                    color="primary"
                    type="checkbox"
                    onClick={() => {
                      const brutto = parseFloat(
                        values.bruttobetrag.replace(".", "").replace(",", ".")
                      );
                      values.rechnungsBetrag = formatNumber(
                        Math.fround(brutto)
                      );
                    }}
                  />
                }
                label={capitalize(t("invoice-amount-deviant"))}
              />
              <Field
                component={TextField}
                name="rechnungsBetrag"
                label={capitalize(t("invoice-amount-euro"))}
                type={"string"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("rechnungsBetrag", e.target.value);
                }}
                onBlur={() => {
                  rechnungBetragChanged();
                }}
                inputProps={{ style: { textAlign: "right" } }}
                disabled={!values.rechnungsbetragAbweichend}
              ></Field>
              <Box
                sx={{
                  gridColumn: { sm: "1 / 5" },
                }}
              >
                <EingangsrechnungenZuweisungsListe
                  korrekturRechnungId={rechnungId}
                  zugewieseneRechnungId={rechnung.verknuepfteRechnung}
                  belegTyp={values.belegtyp}
                  lieferantId={values.lieferant?.id ?? ""}
                  bruttoBetrag={values.bruttobetrag}
                  rechnungsBetrag={values.rechnungsBetrag}
                  rechnungsBetragAbweichend={values.rechnungsbetragAbweichend}
                  values={values}
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        {!isStornoTyp && (
          <>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography>{t("construction-site-assignment")}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <BaustellenEintrag
                  baustellenEintraege={values.baustellenEintrag}
                  rechnungId={rechnungId}
                  dirty={dirty}
                  isValid={isValid}
                  isValidating={isValidating}
                  kundennummer={values.kundennummer}
                  values={values}
                  updateBeleg={updateBeleg}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary
                expandIcon={<ArrowDropDownIcon />}
                aria-controls="panel2-content"
                id="panel2-header"
              >
                <Typography>Rabatte</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: ["1fr", ".6fr 1.25fr 1fr 1fr"],
                    opacity: 1,
                    pointerEvents: "auto",
                    background: "#FFFFFF",
                  }}
                >
                  <SkontoFields
                    bruttoBetrag={values.bruttobetrag}
                    rechnungsBetragChanged={isRechnungsbetragChanged}
                    isAbweichenderRechnungsBetrag={
                      values.rechnungsbetragAbweichend
                    }
                    skontoBetrag={values.skontoBetrag}
                    zahlBetrag={values.zahlbetrag}
                    rechnungsTyp={values.belegtyp}
                  />
                </Box>
              </AccordionDetails>
            </Accordion>
          </>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          <DeleteRechnungenButton id={rechnungId} />
          <div title={tooltipMessage}>
            <SubmitButton
              sx={{ marginTop: 3, ml: 2 }}
              form="details-eingangsrechnung"
              type="submit"
              variant="outlined"
              color="secondary"
              loading={isSubmitting}
              disabled={isValidating || !isValid || !dirty}
              onMouseEnter={() => validateForm()}
            >
              {t("save")}
            </SubmitButton>
          </div>
          {values.pruefStatus === "OFFEN" &&
            !isStornoTyp &&
            values.baustellenEintrag.length > 0 && (
              <Button
                sx={{ marginTop: 3, ml: 2 }}
                variant="contained"
                color="secondary"
                disabled={isValidating || !isValid}
                onClick={async () => {
                  await updateBaustellenZuweisungen(values.baustellenEintrag);
                  if (dirty) {
                    updateBeleg(values).then(() =>
                      navigate(`/eingangsrechnungen/${returnSearch}`)
                    );
                  } else {
                    navigate(`/eingangsrechnungen/${returnSearch}`);
                  }
                }}
              >
                {t("assign")}
              </Button>
            )}
          {rechnung.belegStatus === "FEHLER_BEI_UEBERTRAG_DATEV" && (
            <ResendDatevButton
              disabled={isValidating || !isValid}
              rechnungId={rechnungId}
              rechnung={values}
            />
          )}
        </Box>
      </Paper>
    </EingangsrechnungProvider>
  );
}

function useSaveRechnung(rechnungId: string) {
  const updateRechnung = useUpdateRechnung();
  const queryClient = useQueryClient();
  return useMutation(
    (input: RechnungDaten) => {
      return updateRechnung(rechnungId, input);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["rechnungen"]);
      },
    }
  );
}

function useGetAnhang(
  rechnungId: string,
  options: { enabled?: boolean } = {}
): UseQueryResult<Blob> {
  const getAccessToken = useGetAccessToken();
  return useQuery<Blob>(
    ["attachments", rechnungId],
    async () => {
      const response = await fetch(
        `${apiUrl}/rechnungen/${rechnungId}/anhang`,
        { headers: { authorization: `Bearer ${await getAccessToken()}` } }
      );
      return await response.blob();
    },
    { ...options, suspense: false, cacheTime: 0 }
  );
}

export function formValuesToRechnungData(values: any): RechnungDaten {
  return {
    belegTyp: values.belegtyp,
    kategorie: values.belegtyp.toUpperCase(),
    belegStatus: values.belegStatus,
    rechnungsNummer: values.rechnungsnummer,
    rechnungsDatum: formatAsTimestamp(values.rechnungsdatum),
    bruttoBetrag: formatAsFloat(values.bruttobetrag),
    rechnungsBetrag: formatAsFloat(values.rechnungsBetrag),
    rechnungsbetragAbweichend: values.rechnungsbetragAbweichend,
    nettoBetrag: formatAsFloat(values.nettobetrag),
    lieferantId:
      typeof values.lieferant === "string"
        ? values.lieferant
        : (values.lieferant as Lieferant)?.id ?? "",
    kundenNummer: values.kundennummer,
    steuer: values.steuer,
    steuernummer: values.steuernummer,
    umsatzsteuerId: values.umsatzsteuerId,
    iban: values.iban,
    geschaeftspartnerKonto: values.geschaefspartnerKonto,
    bezahltAm: formatAsTimestamp(values.bezahltAm),
    bezahltStatus: values.bezahltAm ? "BEZAHLT" : "NICHT_BEZAHLT",
    pruefStatus: values.pruefStatus?.toUpperCase() || "OFFEN",
    skontoProzent: formatAsFloat(values.skonto1InProzent),
    skontierfaehigerBetrag: formatAsFloat(values.skontoInEuro),
    skontoBetrag: formatAsFloat(values.skontoBetrag),
    skontoDatum: formatAsTimestamp(values.faelligkeitMitSkonto),
    faelligOhneSkonto: formatAsTimestamp(values.faelligkeitOhneSkonto),
  };
}
